import {
  isEnsembleHeadcountTie,
  isEnsembleMergeMode,
  isTranscriptionMode,
  type TEnsembleHeadcountTie,
  TRANSCRIPTION_LABELS,
  type TTranscriptionMode,
  type TTranscriptionProvider,
  TURNS_CAPABLE_PROVIDERS,
  WORDS_CAPABLE_PROVIDERS,
} from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Group, MultiSelect, Paper, Select, Stack, Switch, Text } from "@mantine/core"
import { useCallback, useMemo } from "react"
import {
  resolveWorkspaceMergeMode,
  resolveWorkspaceTranscriptionMode,
} from "../../../../components/ReprocessForm/helpers"
import { SettingsReadOnlyNotice } from "../SettingsReadOnlyNotice"
import { parseEngineSetting } from "./helpers"
import type { IProps } from "./interfaces"

const TENANT_KEYS = ["ensemble_merge_mode"]

const ADMIN_KEYS = [
  "default_transcription_mode",
  "default_ensemble_word_engines",
  "default_ensemble_turn_engines",
  "default_ensemble_adjudicate_speakers",
  "default_ensemble_headcount_tie",
]

function providerSelectData(ids: readonly TTranscriptionProvider[]): Array<{ value: string; label: string }> {
  return ids.map((id) => ({ value: id, label: TRANSCRIPTION_LABELS[id] }))
}

/**
 * Finished-transcript settings: tenant merge behaviour when the workspace runs
 * the ensemble, plus super-admin engine-list / mode controls on the platform
 * view. Presentational — persistence lives on SettingsView.
 */
export const FinishedTranscriptCard = ({
  settings,
  saving,
  canManage = true,
  isAdminOnly = false,
  updateSetting,
  save,
}: IProps): React.JSX.Element | null => {
  const { i18n } = useLingui()

  const effectiveMode = resolveWorkspaceTranscriptionMode(settings)
  const mergeMode = resolveWorkspaceMergeMode(settings)

  const adminModeRaw = settings.default_transcription_mode ?? "ensemble"
  const adminMode: TTranscriptionMode = isTranscriptionMode(adminModeRaw) ? adminModeRaw : "ensemble"

  const wordEngines = useMemo(
    () => parseEngineSetting("words", settings.default_ensemble_word_engines),
    [settings.default_ensemble_word_engines],
  )
  const turnEngines = useMemo(
    () => parseEngineSetting("turns", settings.default_ensemble_turn_engines),
    [settings.default_ensemble_turn_engines],
  )
  const adjudicateSpeakers = settings.default_ensemble_adjudicate_speakers === "true"
  const headcountRaw = settings.default_ensemble_headcount_tie ?? "lower"
  const headcountTie: TEnsembleHeadcountTie = isEnsembleHeadcountTie(headcountRaw) ? headcountRaw : "lower"

  const wordEngineOptions = useMemo(() => providerSelectData(WORDS_CAPABLE_PROVIDERS), [])
  const turnEngineOptions = useMemo(() => providerSelectData(TURNS_CAPABLE_PROVIDERS), [])

  const handleMergeChange = useCallback(
    (value: string | null): void => {
      if (value && isEnsembleMergeMode(value)) updateSetting("ensemble_merge_mode", value)
    },
    [updateSetting],
  )

  const handleAdminModeChange = useCallback(
    (value: string | null): void => {
      if (value && isTranscriptionMode(value)) updateSetting("default_transcription_mode", value)
    },
    [updateSetting],
  )

  const handleWordEnginesChange = useCallback(
    (values: string[]): void => {
      updateSetting("default_ensemble_word_engines", JSON.stringify(values))
    },
    [updateSetting],
  )

  const handleTurnEnginesChange = useCallback(
    (values: string[]): void => {
      updateSetting("default_ensemble_turn_engines", JSON.stringify(values))
    },
    [updateSetting],
  )

  const handleHeadcountChange = useCallback(
    (value: string | null): void => {
      if (value && isEnsembleHeadcountTie(value)) updateSetting("default_ensemble_headcount_tie", value)
    },
    [updateSetting],
  )

  const handleSave = useCallback((): void => {
    save(isAdminOnly ? ADMIN_KEYS : TENANT_KEYS)
  }, [isAdminOnly, save])

  const showTenantMerge = !isAdminOnly && effectiveMode === "ensemble"
  if (!isAdminOnly && !showTenantMerge) return null

  return (
    <Paper withBorder p="md">
      <Stack gap="lg">
        {showTenantMerge && (
          <div>
            <Select
              label={i18n._(msg`Merge behaviour`)}
              data={[
                {
                  value: "constrained",
                  label: i18n._(msg`Constrained — only choose between what the engines heard (recommended)`),
                },
                {
                  value: "rewrite",
                  label: i18n._(msg`Rewrite — let the AI reword disputed passages`),
                },
              ]}
              value={mergeMode}
              disabled={!canManage}
              onChange={handleMergeChange}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              maw={520}
            />
            <Text size="xs" c="dimmed" mt={6}>
              <Trans>
                Rewrite uses more credits per meeting and can change wording. Constrained never adds words that no
                engine heard.
              </Trans>
            </Text>
          </div>
        )}

        {isAdminOnly && (
          <>
            <Select
              label={i18n._(msg`Finished transcript: engines`)}
              description={i18n._(
                msg`Ensemble is the default: several engines vote on who spoke and agree on the words. Choose Two-pass to run one engine for words and one for speaker turns.`,
              )}
              data={[
                { value: "two-pass", label: i18n._(msg`Two-pass — words from one engine, speaker turns from another`) },
                { value: "ensemble", label: i18n._(msg`Ensemble — several engines vote on words and who spoke`) },
              ]}
              value={adminMode}
              disabled={!canManage}
              onChange={handleAdminModeChange}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              maw={520}
            />

            <MultiSelect
              label={i18n._(msg`Finished transcript: words engines`)}
              description={i18n._(msg`1 to 3 engines. Saved as a JSON list.`)}
              data={wordEngineOptions}
              value={wordEngines}
              disabled={!canManage}
              onChange={handleWordEnginesChange}
              hidePickedOptions
              comboboxProps={{ withinPortal: true }}
              maw={520}
            />

            <div>
              <MultiSelect
                label={i18n._(msg`Finished transcript: speaker-turns engines`)}
                description={i18n._(msg`2 to 4 engines. Saved as a JSON list.`)}
                data={turnEngineOptions}
                value={turnEngines}
                disabled={!canManage}
                onChange={handleTurnEnginesChange}
                hidePickedOptions
                comboboxProps={{ withinPortal: true }}
                maw={520}
              />
              {turnEngines.length === 2 && (
                <Alert variant="light" color="warning" mt="xs">
                  <Text size="sm">
                    <Trans>
                      Two turns engines cannot break a headcount tie on their own; the tie setting below decides.
                    </Trans>
                  </Text>
                </Alert>
              )}
            </div>

            <Group justify="space-between" wrap="nowrap" gap="md">
              <div>
                <Text fw={600}>
                  <Trans>Finished transcript: AI decides disputed speakers</Trans>
                </Text>
                <Text size="xs" c="dimmed">
                  <Trans>
                    When engines disagree on who spoke, ask the AI to pick. Off until the ensemble eval says it helps.
                  </Trans>
                </Text>
              </div>
              <Switch
                checked={adjudicateSpeakers}
                disabled={!canManage}
                onChange={(e) =>
                  updateSetting("default_ensemble_adjudicate_speakers", e.currentTarget.checked ? "true" : "false")
                }
                aria-label={i18n._(msg`Finished transcript: AI decides disputed speakers`)}
              />
            </Group>

            <Select
              label={i18n._(msg`Finished transcript: headcount tie`)}
              description={i18n._(msg`When two speaker-count votes tie, pick the lower or higher number.`)}
              data={[
                { value: "lower", label: i18n._(msg`Lower — fewer speakers`) },
                { value: "higher", label: i18n._(msg`Higher — more speakers`) },
              ]}
              value={headcountTie}
              disabled={!canManage}
              onChange={handleHeadcountChange}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              maw={520}
            />
          </>
        )}

        {canManage ? (
          <Button onClick={handleSave} loading={saving} w="fit-content">
            <Trans>Save</Trans>
          </Button>
        ) : (
          <SettingsReadOnlyNotice />
        )}
      </Stack>
    </Paper>
  )
}
